import { useId, useState } from "react";
import { toast } from "sonner";
import type { ClientSummary, ClientUpdate } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { useClientLogo, useUpdateClient } from "@/hooks/use-clients";
import { refusalDetail } from "@/services/api-client";
import { isComplete } from "./AddClientDialog";
import {
  type ContactDraft,
  KeyContactSection,
  TextField,
  contactChanges,
  contactDraftOf,
} from "./ClientField";
import { LogoField, usePreview } from "./LogoField";

/** What the form does to the logo: leave it, replace it with a file, or take it off. */
type LogoChange =
  { kind: "kept" } | { kind: "chosen"; file: File } | { kind: "removed" };

interface Draft {
  name: string;
  industry: string;
  contact: ContactDraft;
}

/** The fields the attorney changed, and nothing else, so a partial write leaves the rest. */
export function changesTo(client: ClientSummary, draft: Draft): ClientUpdate {
  const body: ClientUpdate = {};
  if (draft.name.trim() !== client.name) body.name = draft.name.trim();
  if (draft.industry.trim() !== client.industry)
    body.industry = draft.industry.trim();
  const contact = contactChanges(client.key_contact, draft.contact);
  if (contact !== undefined) body.key_contact = contact;
  return body;
}

function useSave(client: ClientSummary, onSaved: () => void) {
  const update = useUpdateClient();
  const logo = useClientLogo();
  async function save(draft: Draft, change: LogoChange) {
    const body = changesTo(client, draft);
    try {
      if (Object.keys(body).length > 0) {
        await update.mutateAsync({ clientId: client.client_id, body });
      }
      if (change.kind !== "kept") {
        await logo.mutateAsync({
          clientId: client.client_id,
          file: change.kind === "chosen" ? change.file : null,
        });
      }
      toast.success("Client details saved.");
      onSaved();
    } catch (error) {
      toast.error(
        refusalDetail(
          error,
          "The client could not be saved. Please try again.",
        ),
      );
    }
  }
  return { save, pending: update.isPending || logo.isPending };
}

/**
 * Edit client details: the name, the industry, the logo and the key contact,
 * the one place a client's details are changed, over a partial write that
 * sends only what changed.
 */
export function EditClientDialog({
  client,
  onClose,
}: {
  client: ClientSummary;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => ({
    name: client.name,
    industry: client.industry,
    contact: contactDraftOf(client.key_contact),
  }));
  const [change, setChange] = useState<LogoChange>({ kind: "kept" });
  const preview = usePreview(change.kind === "chosen" ? change.file : null);
  const shownUrl =
    change.kind === "chosen"
      ? preview
      : change.kind === "removed"
        ? null
        : client.logo_url;
  const sentenceId = useId();
  const { save, pending } = useSave(client, onClose);
  const set = (key: "name" | "industry") => (value: string) => {
    setDraft((was) => ({ ...was, [key]: value }));
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby={sentenceId}>
        <DialogTitle>Edit client details</DialogTitle>
        <DialogDescription id={sentenceId}>
          Changes apply everywhere this client appears, and to every draft under
          it.
        </DialogDescription>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save(draft, change);
          }}
        >
          <TextField
            label="Inventor/Organisation name"
            value={draft.name}
            onChange={set("name")}
          />
          <TextField
            label="Industry type"
            value={draft.industry}
            onChange={set("industry")}
          />
          <LogoField
            name={draft.name}
            shownUrl={shownUrl}
            onChoose={(file) => {
              setChange({ kind: "chosen", file });
            }}
            onRemove={() => {
              setChange({ kind: "removed" });
            }}
          />
          <KeyContactSection
            value={draft.contact}
            onChange={(contact) => {
              setDraft((was) => ({ ...was, contact }));
            }}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isComplete(draft) || pending}>
              {pending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
